import { PhoneNumberUtil } from "google-libphonenumber";
import { defaultCountries, parseCountry } from "react-international-phone";

const phoneUtil = PhoneNumberUtil.getInstance();

// Resolve a phone dial code (e.g. "1") from an ISO2 country code (e.g. "US"),
// used to reconstruct the full international value for display.
export const getDialCodeByIso2 = (iso2?: string | null): string => {
  if (!iso2) return "";
  const match = defaultCountries.find(
    (c) => parseCountry(c).iso2 === iso2.toLowerCase()
  );
  return match ? parseCountry(match).dialCode : "";
};

/**
 * True when the stored value carries no subscriber digits.
 *
 * The picker always seeds a dial code, so a client who has never given a phone
 * number is stored as "+1" — digits present, but none of them typed by anyone.
 * Validating that reads as "Enter a valid phone number" against a field the user
 * has not touched. Strip the dial code and judge what is left: nothing means
 * empty, and one digit is enough to hand over to `isPhoneValid`.
 */
export const isPhoneNumberEmpty = (
  phone?: string | null,
  iso2?: string | null
): boolean => {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (!digits) return true;

  const dial = getDialCodeByIso2(iso2).replace(/\D/g, "");
  const subscriber = dial && digits.startsWith(dial)
    ? digits.slice(dial.length)
    : digits;

  return subscriber.length === 0;
};

// True when the full international number (with + dial code) is a valid number.
export const isPhoneValid = (phone?: string | null): boolean => {
  if (!phone) return false;
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
};

/**
 * Rebuild the full international number from what the profile stores.
 *
 * The phone field keeps the national number in `phone` (dial code stripped)
 * and the ISO2 country in `countryCode`. Legacy values may still carry a
 * leading "+"; those are already complete and pass through unchanged.
 */
export const toInternationalPhone = (
  phone?: string | null,
  iso2?: string | null
): string => {
  const stored = String(phone ?? "");
  if (stored.startsWith("+")) return stored;
  const dial = getDialCodeByIso2(iso2);
  return dial ? `+${dial}${stored}` : stored;
};

/**
 * The single verdict on a stored phone number, shared by the field hint and
 * every Save gate so they can never disagree.
 *
 * - `null`  — nothing entered (or only the seeded dial code): not an error.
 * - `true`  — a valid number.
 * - `false` — digits were entered but they don't form a valid number.
 *
 * Validating the stored national number on its own is wrong: libphonenumber
 * has no region to parse it against and rejects every number without a "+".
 */
export const getStoredPhoneValidity = (
  phone?: string | null,
  iso2?: string | null
): boolean | null => {
  const full = toInternationalPhone(phone, iso2);
  if (isPhoneNumberEmpty(full, iso2)) return null;
  return isPhoneValid(full);
};
